// Why the dashboard last sent this browser to /login, so the login page can say
// it. Kept in both storages: localStorage survives a closed tab, sessionStorage
// survives something wiping localStorage (the very case where the token vanished).

const REASON_KEY = 'zeywin-logout-reason';
// Set while this tab holds a session. If it is still here but the token is gone,
// the token was removed from storage behind the dashboard's back.
const HAD_SESSION_KEY = 'zeywin-had-session';

export interface LogoutReason {
  kind: 'unauthorized' | 'manual' | 'storage';
  at: string;
  status?: number;
  method?: string;
  path?: string;
  message?: string;
}

function write(storage: Storage, key: string, value: string | null) {
  try {
    if (value === null) storage.removeItem(key);
    else storage.setItem(key, value);
  } catch {
    // Storage blocked or full — the reason is a nicety, never fail on it.
  }
}

function read(storage: Storage, key: string): string | null {
  try {
    return storage.getItem(key);
  } catch {
    return null;
  }
}

export function recordLogout(reason: Omit<LogoutReason, 'at'>) {
  const value = JSON.stringify({ ...reason, at: new Date().toISOString() });
  write(localStorage, REASON_KEY, value);
  write(sessionStorage, REASON_KEY, value);
  write(sessionStorage, HAD_SESSION_KEY, null);
}

/** A 401 from the API: record which request it was, then go to /login. */
export function recordUnauthorized(method: string, url: string, message?: string) {
  let path = url;
  try {
    path = new URL(url, window.location.origin).pathname;
  } catch {
    // keep the raw string
  }
  recordLogout({ kind: 'unauthorized', status: 401, method: method.toUpperCase(), path, message });
}

/** A protected page rendered with a token: this tab holds a session. */
export function markSessionActive() {
  if (read(sessionStorage, HAD_SESSION_KEY) !== '1') write(sessionStorage, HAD_SESSION_KEY, '1');
}

/** A successful sign-in: the last reason has been read, forget it. */
export function clearLogoutReason() {
  write(localStorage, REASON_KEY, null);
  write(sessionStorage, REASON_KEY, null);
}

/**
 * Called when a protected page finds no token. If this tab had a session and
 * nothing recorded a logout, the token disappeared from storage on its own.
 */
export function noteMissingToken() {
  if (read(sessionStorage, HAD_SESSION_KEY) !== '1') return;
  if (read(sessionStorage, REASON_KEY) || read(localStorage, REASON_KEY)) return;
  recordLogout({ kind: 'storage' });
}

export function readLogoutReason(): LogoutReason | null {
  const raw = read(sessionStorage, REASON_KEY) ?? read(localStorage, REASON_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as LogoutReason;
  } catch {
    return null;
  }
}
